/**
 * Action Plan task drawer — the shared selected-task detail surface (2B part 2).
 *
 * Rendered by `ActionPlanPanel` beside the view panel, so selection is canonical: the
 * same drawer serves every view, driven by the URL's `?apTask=`. Everything here is
 * persistent — nothing depends on hover, and no fact is conveyed by colour alone.
 *
 * FOCUS CONTRACT (documented because tests pin it):
 *
 * - Task cards that open the drawer expose `aria-expanded` and
 *   `aria-controls={AP_TASK_DRAWER_ID}`, and carry `data-ap-task-button={task.id}`.
 * - Opening (or switching selection) moves focus to the CLOSE BUTTON — a predictable,
 *   labelled target at the top of the drawer.
 * - Closing returns focus to the invoking task card, located by its
 *   `data-ap-task-button` attribute, when it still exists in the document.
 * - When no invoking element exists — the drawer was opened from a deep link
 *   (`?apTask=` in a shared URL) or the card was filtered out meanwhile — closing falls
 *   back to the selected Action Plan view tab, which is always rendered while the plan
 *   is on screen. The fallback is deliberate: focus must land somewhere real, and the
 *   tablist is the surface the reader navigates views with.
 *
 * VALUES ARE RENDERED THROUGH THE PROVENANCE CONTRACT: an unpriced task shows the
 * unavailable state and the basis for it, never a zero.
 */

import React, { useEffect, useRef } from 'react';
import {
  AP_TASK_DRAWER_ID,
  EFFORT_UNAVAILABLE_LABEL,
  UNAVAILABLE_LABEL,
  formatCurrency,
  formatEffortWeeks,
  getStatusLabel,
} from './actionPlanValueFormat';
import { PROVENANCE_LABEL } from '../types/actionPlanValueProvenance';
import {
  MATRIX_EFFORT_BOUNDARY_WEEKS,
  hasFiniteValue,
  provenanceOf,
} from './actionPlanMatrixPlacement';
import type { Phase, Task } from '../types/actionPlan';

interface ActionPlanTaskDrawerProps {
  /** The selected task. The panel renders the drawer only when one is selected. */
  task: Task;
  /** The full rendered task set, for resolving dependency ids to titles. */
  tasks: Task[];
  phases: Phase[];
  /** Clear the selection. The drawer handles focus return itself, before calling this. */
  onClose: () => void;
}

/** The drawer's effort description: the number and which side of the boundary it sits. */
function effortDescription(effortWeeks: number | null | undefined): string {
  const formatted = formatEffortWeeks(effortWeeks);
  if (formatted === EFFORT_UNAVAILABLE_LABEL) return formatted;
  const numeric = typeof effortWeeks === 'number' ? effortWeeks : 0;
  return numeric < MATRIX_EFFORT_BOUNDARY_WEEKS
    ? `${formatted} — low effort (under ${MATRIX_EFFORT_BOUNDARY_WEEKS} wk)`
    : `${formatted} — high effort (${MATRIX_EFFORT_BOUNDARY_WEEKS} wk or more)`;
}

export const ActionPlanTaskDrawer: React.FC<ActionPlanTaskDrawerProps> = ({
  task,
  tasks,
  phases,
  onClose,
}) => {
  const closeButtonRef = useRef<HTMLButtonElement | null>(null);

  /*
   * Focus the close button whenever the drawer opens OR the selection moves to a
   * different task — both are "the drawer now shows something new" moments, and the
   * close button is the predictable landing point.
   */
  useEffect(() => {
    closeButtonRef.current?.focus();
  }, [task.id]);

  const handleClose = () => {
    /*
     * Resolve the focus target BEFORE the drawer unmounts. The invoking card, when it
     * still exists; otherwise the documented fallback — the selected view tab.
     */
    const invoker = document.querySelector<HTMLElement>(
      `[data-ap-task-button="${CSS.escape(task.id)}"]`,
    );
    const fallback = document.querySelector<HTMLElement>(
      '.ap-view-tabs [role="tab"][aria-selected="true"]',
    );
    (invoker ?? fallback)?.focus();
    onClose();
  };

  const handleKeyDown = (event: React.KeyboardEvent) => {
    if (event.key === 'Escape') {
      event.stopPropagation();
      handleClose();
    }
  };

  const phase = phases.find((p) => p.id === task.phase_id) ?? null;
  const provenance = provenanceOf(task);
  /*
   * Finite gate, not the generic provenance gate (36cd088 follow-up): `hasValue` lets a
   * NaN or Infinity through when its provenance claims otherwise, and this row would
   * then describe an unusable number as a valid forecast. `provenanceOf` above applies
   * the same boundary, so the value and its source row cannot disagree.
   */
  const priced = hasFiniteValue(task.value_usd);

  const dependencyTitles = task.dependencies.map((depId) => {
    const dep = tasks.find((t) => t.id === depId);
    return { id: depId, title: dep ? dep.title : `Task ${depId} (not in this plan)` };
  });

  return (
    // Escape-to-close on the container supplements the labelled close button; it is
    // never the only close path, so the container needs no interactive role.
    <aside
      id={AP_TASK_DRAWER_ID}
      className="ap-task-drawer"
      role="complementary"
      aria-labelledby="ap-task-drawer-title"
      onKeyDown={handleKeyDown}
    >
      <div className="ap-task-drawer__header">
        <h4 id="ap-task-drawer-title">{task.title}</h4>
        <button
          type="button"
          ref={closeButtonRef}
          className="ap-task-drawer__close"
          aria-label={`Close task details for ${task.title}`}
          onClick={handleClose}
        >
          Close
        </button>
      </div>

      {task.description && (
        <p className="ap-task-drawer__description">{task.description}</p>
      )}

      <dl className="ap-task-drawer__facts">
        <dt>Phase</dt>
        <dd>{phase ? phase.name : 'Not assigned to a phase'}</dd>

        <dt>Status</dt>
        <dd>{getStatusLabel(task.status)}</dd>

        <dt>Effort</dt>
        <dd>{effortDescription(task.effort_weeks)}</dd>

        <dt>Priority</dt>
        <dd>{task.priority.charAt(0).toUpperCase() + task.priority.slice(1)}</dd>

        {/* Named measure (S4g): this is the ANNUAL forecast — the 3-yr NPV shown in
            the value derivation panel is a derived view of this same basis, not a
            second estimate, and the two must never share an ambiguous label. */}
        <dt>Annual forecast</dt>
        <dd>
          {priced ? formatCurrency(task.value_usd.value) : UNAVAILABLE_LABEL}
          <span className="ap-task-drawer__quiet">
            {priced
              ? 'Forecast, not realised value — nothing here measures realisation.'
              : task.value_usd?.basis ??
                'No usable value is stored for this task, which is different from zero.'}
          </span>
        </dd>

        <dt>Value source</dt>
        <dd>{PROVENANCE_LABEL[provenance]}</dd>

        <dt>Critical path</dt>
        <dd>
          {task.is_critical_path ? 'On the critical path' : 'Not on the critical path'}
        </dd>

        <dt>Dependencies</dt>
        <dd>
          {dependencyTitles.length === 0 ? (
            'None'
          ) : (
            <ul className="ap-task-drawer__deps">
              {dependencyTitles.map((dep) => (
                <li key={dep.id}>{dep.title}</li>
              ))}
            </ul>
          )}
        </dd>
      </dl>
    </aside>
  );
};
